import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
  ChakraProvider,
  Select,
  useDisclosure,
  Tooltip,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import analytics from '../../../../segmentTracking';
import CopyPasteIcon from 'assets/icons/CopyPasteIcon';
import CustomSelect from 'components/peoplePage/CustomSelect';
import { useGate } from 'statsig-react';
import EditModal from './EditModal';
import DeleteModal from './DeleteModal';
import { Switch } from '../../../../components/Switch/index';
import { Input } from '../../../../components/Input/index';
import api from '../../../../utils/api';
import PaginationControls from '../AcademicManagement/components/PaginationControls';

const UsersTab = ({ refreshTrigger }) => {
  const { value: toggleShareLink } = useGate('settings_display_share_link');
  const [copyLink, setCopyLink] = useState('');
  const [sendInviteEmail, setSendInvite] = useState('');
  const [shareLinkSwitch, setShareLinkSwitch] = useState(true);
  const inputRef = useRef(null);
  const [checkValidEmail, setCheckValidEmail] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalMembers, setTotalMembers] = useState(0);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [teamMember, setTeamMember] = useState([]);
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [editUser, setEditUser] = useState({});
  const usersOptions = [
    { value: 'all', display: 'All members' },
    { value: 'admin', display: 'Admin' },
    { value: 'member', display: 'Member' },
    { value: 'instructor', display: 'Instructor' },
  ];
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [role, setRole] = useState('');

  const handleChange = (event) => {
    // Track role selection
    analytics.track('settings:team:users:role:selected', {
      role: event.target.value,
      email: currentUser.email,
      userRole: currentUser?.role,
    });
    setRole(event.target.value);
  };

  const handleCopy = () => {
    inputRef.current.select();
    navigator.clipboard.writeText(copyLink);
  };

  const handleSelectedStatusChange = (value) => {
    // Track status filter selection
    analytics.track('settings:team:users:status:filtered', {
      status: value,
      email: currentUser.email,
      userRole: currentUser?.role,
    });
    setSelectedStatus(value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleInviteSendViaEmail = async (e) => {
    e.preventDefault();

    // Track send invite action
    analytics.track('settings:team:users:invite:sent', {
      email: sendInviteEmail,
      role: role,
      senderEmail: currentUser.email,
      userRole: currentUser?.role,
    });

    setIsLoading(true);

    try {
      // Ensure you are getting the correct token
      const currentInstitution = JSON.parse(
        localStorage.getItem('institution'),
      );
      let body = {};
      if (sendInviteEmail) {
        body.email = sendInviteEmail;
      }
      body.institution = currentInstitution?._id;
      if (role) {
        body.role = role;
      }

      const response = await api.post(`/dashboard/users/inviteUser`, body);

      const { message } = response.data;

      toast({
        title: 'Invite Sent',
        description: message || 'Invitation has been sent successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setRole('');
      setSendInvite('');
      // Refresh team members list
      await fetchTeamMembers();
    } catch (error) {
      console.log('invite error', error);

      toast({
        title: 'Error',
        description:
          error.response?.data?.message ||
          'An error occurred while sending the invite.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  function validateEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  const fetchTeamMembers = useCallback(async () => {
    setIsLoading(true);
    try {
      const { _id } = JSON.parse(localStorage.getItem('institution'));
      const skip = (currentPage - 1) * itemsPerPage;

      // Build query parameters
      const params = new URLSearchParams({
        institution: _id,
        skip: skip.toString(),
        limit: itemsPerPage.toString(),
      });

      // Add role filter if not 'all'
      if (selectedStatus !== 'all') {
        params.append('role', selectedStatus);
      }

      // Add search parameter if search term exists and is >= 2 characters
      if (debouncedSearchTerm && debouncedSearchTerm.length >= 2) {
        params.append('search', debouncedSearchTerm);
      }

      const response = await api.get(`/dashboard/teams?${params.toString()}`);

      if (response?.data?.success) {
        setTeamMember(
          response?.data?.data?.members || response?.data?.data || [],
        );
        setTotalMembers(
          response?.data?.data?.total || response?.data?.total || 0,
        );

        // Track team members view
        analytics.track('settings:team:users:viewed', {
          membersCount:
            response?.data?.data?.members?.length ||
            response?.data?.data?.length ||
            0,
          totalMembers:
            response?.data?.data?.total || response?.data?.total || 0,
          currentPage,
          itemsPerPage,
          searchTerm: debouncedSearchTerm,
          roleFilter: selectedStatus,
          userRole: currentUser?.role,
          email: currentUser?.email,
        });
      } else {
        console.log('Network error');
        setTeamMember([]);
        setTotalMembers(0);
      }
    } catch (error) {
      console.log(`Error -> ${error.message}`);
      setTeamMember([]);
      setTotalMembers(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearchTerm,
    selectedStatus,
    currentUser?.role,
    currentUser?.email,
  ]);

  // Debounce search term - only trigger search after 500ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only set debounced search if term is >= 2 characters or empty
      if (searchTerm.length >= 2 || searchTerm.length === 0) {
        setDebouncedSearchTerm(searchTerm);
        setCurrentPage(1); // Reset to first page when searching
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Search status for tooltip feedback
  const searchStatus = useMemo(() => {
    if (searchTerm === '') {
      return { message: '', showTooltip: false };
    } else if (searchTerm.length < 2) {
      return {
        message: 'Type at least 2 characters to search...',
        showTooltip: true,
      };
    } else {
      return { message: '', showTooltip: false };
    }
  }, [searchTerm]);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchTeamMembers();
    }
  }, [refreshTrigger, fetchTeamMembers]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  useEffect(() => {
    setCheckValidEmail(validateEmail(sendInviteEmail));
  }, [sendInviteEmail]);

  async function handleEditName(name) {
    // Track edit name action
    analytics.track('settings:team:users:name:submitted', {
      editorEmail: currentUser.email,
      memberEmail: editUser.email,
      userRole: currentUser?.role,
      email: currentUser?.email,
    });

    try {
      const response = await api.patch(`/dashboard/teams/updateUser`, {
        name: name,
        userId: editUser?._id,
      });

      if (response?.data?.success) {
        await fetchTeamMembers();
      } else {
        console.log('Network error');
      }
    } catch (error) {
      console.log(`Error -> ${error.message}`);
    }
  }

  async function handleDeleteAdmin() {
    // Track delete action
    analytics.track('settings:team:users:delete:submitted', {
      editorEmail: currentUser.email,
      memberEmail: editUser.email,
      userRole: currentUser?.role,
      email: currentUser?.email,
    });

    try {
      const response = await api.delete(
        `/dashboard/teams/deleteAdmin?id=${editUser?._id}`,
      );

      if (response?.data?.success) {
        toast({
          title: 'Admin Deleted Successfully',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        setTimeout(async () => {
          await fetchTeamMembers();
        }, 2000);
      } else {
        console.log('Network error');
      }
    } catch (error) {
      console.log(`Error -> ${error.message}`);
    }
  }

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  return (
    <Box>
      {/* Invite Members */}
      <Box paddingBottom={'48px'} borderBottom={'1px solid #E7E7E7'}>
        <Box>
          <Text fontSize={'20px'} fontWeight={'700'} lineHeight={'24px'}>
            Invite members
          </Text>
        </Box>
        {toggleShareLink && (
          <>
            <Flex
              justifyContent={'space-between'}
              alignItems={'center'}
              margin={'12px 0'}
            >
              <Box>
                <Text>Share this link to invite others to Twella</Text>
              </Box>

              <Box>
                <Switch
                  checked={shareLinkSwitch}
                  onCheckedChange={() => setShareLinkSwitch(!shareLinkSwitch)}
                />
              </Box>
            </Flex>
            <Flex>
              <Box flexBasis={'90%'}>
                <Input
                  ref={inputRef}
                  name="copyLink"
                  id="copyLink"
                  value={copyLink}
                  onChange={(e) => setCopyLink(e.target.value)}
                  placeholder="https://twella.app/"
                  style={{
                    outline: 'none',
                    border: '1.1px solid #E7E7E7',
                    background: 'transparent',
                    padding: '5px 12px',
                    width: '100%',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                  }}
                />
              </Box>
              <Flex
                bg={'#EF6506'}
                justifyContent={'center'}
                alignItems={'center'}
                padding={'0 8px'}
                flexBasis={'10%'}
                borderRightRadius={'8px'}
                onClick={handleCopy}
                cursor={'pointer'}
              >
                <Box>
                  <CopyPasteIcon />
                </Box>
                <Box>
                  <Text
                    color={'#FFFDFB'}
                    fontWeight={'700'}
                    fontSize={'14px'}
                    lineHeight={'20px'}
                    marginLeft={'2px'}
                  >
                    Copy
                  </Text>
                </Box>
              </Flex>
            </Flex>
          </>
        )}

        {currentUser?.role !== 'member' ? (
          <>
            <Box pt={'16px'}>
              <Text>Send Twella email invites</Text>
            </Box>
            {/* Send invite input  */}
            <Flex pt={'8px'} gap="4">
              <Box flexBasis={'60%'}>
                <Input
                  name="sendInviteEmail"
                  id="sendInviteEmail"
                  type="email"
                  value={sendInviteEmail}
                  onFocus={() => {
                    // Track email input focus (clicked)
                    analytics.track(
                      'settings:team:users:invite:email:clicked',
                      {
                        email: currentUser.email,
                        inputType: 'invite_email',
                        userRole: currentUser?.role,
                      },
                    );
                  }}
                  onChange={(e) => setSendInvite(e.target.value)}
                  placeholder="mymail@gmail.com"
                  style={{
                    outline: 'none',
                    border: '1.1px solid #E7E7E7',
                    background: 'transparent',
                    padding: '5px 12px',
                    width: '100%',
                    borderRadius: '8px',
                  }}
                />
              </Box>
              <Box flexBasis={'25%'}>
                <ChakraProvider>
                  <Box>
                    <Select
                      required={true}
                      h={'35px'}
                      placeholder="Select Role"
                      onChange={handleChange}
                      value={role}
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                      <option value="instructor">Instructor</option>
                    </Select>
                  </Box>
                </ChakraProvider>
              </Box>
              <Flex
                bg={checkValidEmail && role ? '#EF6506' : '#EF650680'}
                justifyContent="center"
                alignItems="center"
                padding="0 8px"
                flexBasis="15%"
                borderRightRadius="8px"
                onClick={
                  checkValidEmail && role ? handleInviteSendViaEmail : undefined
                }
                cursor={checkValidEmail && role ? 'pointer' : 'not-allowed'}
                opacity={checkValidEmail && role ? 1 : 0.5}
                pointerEvents={checkValidEmail && role ? 'auto' : 'none'}
              >
                <Box>
                  <Text
                    color="#FFFDFB"
                    fontWeight="700"
                    fontSize="14px"
                    lineHeight="20px"
                    ml="4px"
                  >
                    Send invite
                  </Text>
                </Box>
              </Flex>
            </Flex>
          </>
        ) : (
          ''
        )}
      </Box>

      {/* Manage Members */}
      <Box marginTop={'32px'}>
        <Box>
          <Text fontSize={'20px'} fontWeight={'700'} lineHeight={'24px'}>
            Manage members
          </Text>
        </Box>

        <Box mt={'24px'}>
          <Flex mb={4} alignItems="center">
            <Box mr={'8px'} position="relative" width="200px">
              <Tooltip
                label={searchStatus.message}
                isOpen={searchStatus.showTooltip}
                placement="bottom"
                bg="gray.700"
                color="white"
                fontSize="12px"
                px={3}
                py={2}
                borderRadius="6px"
                hasArrow
                gutter={8}
              >
                <Box width="100%">
                  <Input
                    placeholder="Search by name"
                    value={searchTerm}
                    onFocus={() => {
                      // Track search input focus (clicked)
                      analytics.track('settings:team:users:search:clicked', {
                        email: currentUser.email,
                        inputType: 'search_name',
                        userRole: currentUser?.role,
                      });
                    }}
                    className="TextInput"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      height: '40px',
                      border: '1.1px solid #E7E7E7',
                      borderRadius: '8px',
                      padding: '0 12px',
                      fontSize: '14px',
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>

            <Box mr={'8px'}>
              <CustomSelect
                options={usersOptions}
                width="150px"
                height="40px"
                placeholder={'All members'}
                value={selectedStatus}
                onChange={(newStatus) => handleSelectedStatusChange(newStatus)}
              />
            </Box>

            <Box mr={'8px'}>
              <CustomSelect
                options={[
                  { value: 5, display: '5 per page' },
                  { value: 10, display: '10 per page' },
                  { value: 25, display: '25 per page' },
                  { value: 50, display: '50 per page' },
                ]}
                width="120px"
                height="40px"
                placeholder={'10 per page'}
                value={itemsPerPage}
                onChange={(newItemsPerPage) =>
                  handleItemsPerPageChange(newItemsPerPage)
                }
              />
            </Box>
          </Flex>
          <Box border={'1px solid #E7E7E7'} borderRadius={'12px'}>
            <Table>
              <Thead borderBottomWidth={2} borderBottomColor="#E7E7E7">
                <Tr>
                  <Th>Name</Th>
                  <Th borderLeft={'1px solid #E7E7E7'}>
                    <Text>Email</Text>
                  </Th>
                  <Th borderLeft={'1px solid #E7E7E7'}>Role</Th>
                  {currentUser?.role === 'super_admin' ? (
                    <Th borderLeft={'1px solid #E7E7E7'}>Action</Th>
                  ) : (
                    ''
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {teamMember?.map((member, index) => {
                  const capSuperAdmin = member?.role
                    .split('_')
                    .map(
                      (word) =>
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase(),
                    )
                    .join(' ');

                  return (
                    <Tr key={index}>
                      <Td borderColor="#E7E7E7">{member?.fullName}</Td>
                      <Td borderColor="#E7E7E7">{member?.email}</Td>
                      <Td borderColor="#E7E7E7">{capSuperAdmin}</Td>
                      {currentUser?.role === 'super_admin' ? (
                        <Td borderColor="#E7E7E7">
                          <EditIcon
                            cursor={'pointer'}
                            onClick={() => {
                              // Track edit action click
                              analytics.track(
                                'settings:team:users:action:clicked',
                                {
                                  editorEmail: currentUser.email,
                                  memberEmail: member.email,
                                  userRole: currentUser?.role,
                                  email: currentUser?.email,
                                },
                              );
                              setEditUser(member);
                              onOpen();
                            }}
                          />
                          <DeleteIcon
                            cursor={'pointer'}
                            marginLeft={'5px'}
                            color={'red'}
                            onClick={() => {
                              // Track delete action click
                              analytics.track(
                                'settings:team:users:delete:clicked',
                                {
                                  editorEmail: currentUser.email,
                                  memberEmail: member.email,
                                  userRole: currentUser?.role,
                                  email: currentUser?.email,
                                },
                              );
                              onDeleteOpen();
                              setEditUser(member);
                            }}
                          />
                        </Td>
                      ) : (
                        ''
                      )}
                    </Tr>
                  );
                })}

                {teamMember?.length === 0 && !isLoading && (
                  <Tr>
                    <Td colSpan="4" textAlign="center" borderColor="#E7E7E7">
                      {debouncedSearchTerm || selectedStatus !== 'all'
                        ? 'No members found matching your criteria.'
                        : 'No team members found.'}
                    </Td>
                  </Tr>
                )}

                {isLoading && (
                  <Tr>
                    <Td colSpan="4" textAlign="center" borderColor="#E7E7E7">
                      Loading...
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination Controls */}
          <PaginationControls
            currentPage={currentPage}
            totalItems={totalMembers}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            entityName="members"
            analyticsPrefix="team:users"
            loading={isLoading}
          />
        </Box>
      </Box>
      <EditModal
        isOpen={isOpen}
        onClose={onClose}
        data={editUser}
        handleEditName={handleEditName}
      />
      <DeleteModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        handleDelete={handleDeleteAdmin}
      />
    </Box>
  );
};

export default UsersTab;
